import React, { useMemo, useEffect, useRef, useState } from "react";
import Popup from "./Popup";
import {
  sampleStatus as sampleStatusList,
  sampleStatusColor,
  sampleStatusLabel,
  caseStatus,
  DATE_TIME_FORMAT_2,
} from "app/constants";
import PrimaryBtn from "../../../UI/Button/PrimaryBtn";
import { projectServices } from "../../../../services/api";
import { toast } from "react-toastify";
import messages from "../../../../services/api/messages";
import PageLoadingBackdrop from "../../../CommonPage/PageLoadingBackdrop";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import InputText from "../../../UI/Form/Input/InputText";
import { useFormik } from "formik";
import moment from "moment";
import * as Yup from "yup";

const InfoRow = ({ label, value }) => {
  return (
    <div className={`w-full flex items-start mt-4`}>
      <label className="h-full pr-4 flex items-center w-[180px]">
        <span className="w-full text-sm leading-[22px] tracking-[.2em] whitespace-nowrap">
          {label}
        </span>
      </label>
      <div className="h-full relative min-w-[100px] w-[calc(100%-180px)] break-all">
        {value}
      </div>
    </div>
  );
};

function SampleCell(props) {
  const { width, sampleStatus, caseInfo, setCaseInfo, highlight } = props;
  const [anchor, setAnchor] = useState(null);

  const [isUpdating, setIsUpdating] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [isShowModalRejected, setIsShowModalRejected] = useState(false);

  const disabledChangeSampleStatus = useMemo(() => {
    return (
      (caseInfo.caseStatus === caseStatus.completed ||
        caseInfo.caseStatus === caseStatus.lost) &&
      sampleStatus !== sampleStatusList.confirmed
    );
  }, [caseInfo, sampleStatus]);

  const isRejected = useMemo(() => {
    return sampleStatus === sampleStatusList.rejected;
  }, [sampleStatus]);

  const canUpdateSampleStatus = useMemo(() => {
    return (
      sampleStatus === sampleStatusList.notStarted ||
      sampleStatus === sampleStatusList.rejected ||
      sampleStatus === sampleStatusList.submitted
    );
  }, [sampleStatus]);
  const ref = useRef();

  useEffect(() => {
    if (highlight) {
      ref?.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [highlight]);

  const handleRightClickSample = (e) => {
    e.preventDefault();
    if (disabledChangeSampleStatus) return;
    setAnchor(e.target);
  };

  const handleUpdateSampleStatusToConfirmed = async (values) => {
    if (isUpdating) return;
    if (!canUpdateSampleStatus) return;
    setIsUpdating(true);

    let data = {
      id: caseInfo._id,
      statusType: "sampleStatus",
      value: sampleStatusList.confirmed,
      sampleInquiryNumber: values.inquiryNumber,
      sampleNote: values.note,
    };

    if (isRejected && !values.inquiryNumber) {
      data.comment = values.comment;
    }

    try {
      const res = await projectServices.updateStatus(data);
      const newCaseInfo = res?.data?.result;
      if (!newCaseInfo?._id) throw Error("Update error!");

      setIsUpdating(false);
      setAnchor(null);
      setOpenModal(false);
      setIsShowModalRejected(false);
      setCaseInfo({
        ...caseInfo,
        sampleStatus: newCaseInfo?.sampleStatus,
        caseStatus: newCaseInfo?.caseStatus,
        sampleInquiryNumber: newCaseInfo?.sampleInquiryNumber,
        sampleNote: newCaseInfo?.sampleNote,
        sampleShippingDate: newCaseInfo?.sampleShippingDate,
      });
    } catch (error) {
      toast.error(messages.callApiFail);
      setIsUpdating(false);
    } finally {
      setIsUpdating(false);
    }
  };

  const {
    handleSubmit,
    resetForm,
    errors,
    values,
    setFieldValue,
    touched,
    handleBlur, // setFieldTouched,
  } = useFormik({
    initialValues: {
      inquiryNumber: "",
      note: "",
      comment: "",
    },
    onSubmit: handleUpdateSampleStatusToConfirmed,
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      inquiryNumber: isRejected
        ? Yup.string().notRequired()
        : Yup.string("送り状・問合せ番号を入力してください。").required(
            "送り状・問合せ番号を入力してください。"
          ),
    }),
  });

  const showModal = () => {
    setIsShowModalRejected(false);
    setOpenModal(true);
    setAnchor(null);
    resetForm();
  };

  const showModalRejected = () => {
    setIsShowModalRejected(true);
    setOpenModal(false);
    setAnchor(null);
    resetForm();
  };

  const closeModal = () => {
    setOpenModal(false);
    setAnchor(null);
    setIsShowModalRejected(false);
  };

  const renderContentPopup = () => {
    if (isShowModalRejected && sampleStatus === sampleStatusList.rejected) {
      return (
        <>
          <div className="mb-4">再対応依頼 コメント</div>
          <InputText
            name="comment"
            value={values.comment}
            onChange={(value) => setFieldValue("comment", value)}
            handleBlur={handleBlur}
            errors={errors.comment}
            touched={touched.comment}
          />
        </>
      );
    }

    if (canUpdateSampleStatus) {
      return (
        <>
          <InputText
            name="inquiryNumber"
            value={values.inquiryNumber}
            onChange={(newValue) => setFieldValue(`inquiryNumber`, newValue)}
            label="送り状・問合せ番号"
            labelWidth={160}
            labelDirection={"ltr"}
            handleBlur={handleBlur}
            errors={errors.inquiryNumber}
            touched={touched.inquiryNumber}
          />
          <InputText
            name="note"
            value={values.note}
            onChange={(newValue) => setFieldValue(`note`, newValue)}
            label="備考"
            labelWidth={160}
            labelDirection={"ltr"}
            handleBlur={handleBlur}
            errors={errors.note}
            touched={touched.note}
            className="mt-2"
          />
        </>
      );
    }

    return (
      <>
        <InfoRow
          label={"発送日"}
          value={
            caseInfo?.sampleShippingDate
              ? moment(caseInfo.sampleShippingDate).format(DATE_TIME_FORMAT_2)
              : ""
          }
        />
        <InfoRow
          label={"送り状・問合せ番号"}
          value={caseInfo?.sampleInquiryNumber || ""}
        />
        <InfoRow label={"備考"} value={caseInfo?.sampleNote || ""} />
      </>
    );
  };

  const renderButtonDialog = () => {
    if (isShowModalRejected && sampleStatus === sampleStatusList.rejected) {
      return (
        <>
          <PrimaryBtn text={"送信"} onClick={handleSubmit} />
          <PrimaryBtn text={"キャンセル"} onClick={closeModal} />
        </>
      );
    }

    if (canUpdateSampleStatus) {
      return (
        <>
          <PrimaryBtn text={"提出"} onClick={handleSubmit} />
          <PrimaryBtn text={"キャンセル"} onClick={closeModal} />
        </>
      );
    }
    return <PrimaryBtn text={"閉じる"} onClick={closeModal} />;
  };

  return (
    <>
      <div
        ref={ref}
        className={`h-full border border-[#B3B3B3] px-2 py-4 shrink-0 ${
          highlight && "bg-yellow-100"
        }`}
        style={{
          width: width,
        }}
      >
        <p className="text-sm leading-[18px] tracking-[.2em] truncate text-center">
          <button
            style={{
              color: sampleStatusColor[sampleStatus],
              cursor: "pointer",
            }}
            onContextMenu={handleRightClickSample}
          >
            {sampleStatusLabel[sampleStatus]}
          </button>
        </p>
      </div>

      <Popup
        id={`sample-${caseInfo._id}`}
        anchor={anchor}
        open={Boolean(anchor)}
      >
        { sampleStatus === sampleStatusList.rejected &&
          <p className="pb-4">
            再依頼コメント:{caseInfo?.sampleComment || ""}
          </p>
         }
        <div className="flex gap-2">
          <PrimaryBtn text="配送手配済み" onClick={showModal} />
          {sampleStatus === sampleStatusList.rejected && (
            <PrimaryBtn text="再対応依頼" onClick={showModalRejected} />
          )}
          <PrimaryBtn text="戻る" onClick={() => setAnchor(null)} />
        </div>
      </Popup>
      <PageLoadingBackdrop open={isUpdating} />
      <Dialog
        open={openModal || isShowModalRejected}
        onClose={() => setOpenModal(false)}
      >
        {!isShowModalRejected && (
          <DialogTitle sx={{ pl: showModalRejected ? "16px" : "60px", fontWeight: "800" }}>
            サンプル発送
          </DialogTitle>
        )}
        <DialogContent
          sx={{
            pl: showModalRejected ? "16px" : "60px",
            pr: !canUpdateSampleStatus ? "60px" : "",
          }}
        >
          {renderContentPopup()}
        </DialogContent>

        <DialogActions>{renderButtonDialog()}</DialogActions>
      </Dialog>
    </>
  );
}

export default SampleCell;
